import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import { adminApi, podsApi } from "../axios"

const EMPTY = { email: "", name: "", owner: null, podId: "" }

function errorText(err) {
  const data = err?.response?.data
  if (!data || typeof data !== "object") return "Could not create the employee."
  return Object.entries(data)
    .flatMap(([field, value]) =>
      (Array.isArray(value) ? value : [value]).map((item) =>
        field === "non_field_errors" ? String(item) : `${field}: ${item}`
      )
    )
    .join(" ")
}

/**
 * Texting-only employee: logs in with a magic link, texts (and dials, if in a
 * pod) for the company account. No number is bought here; they pick one after
 * logging in.
 */
export default function AdminSmsEmployeeCreateDialog({
  open,
  onClose,
  onCreate,
  saving,
  podDialerOn = true,
}) {
  const [form, setForm] = useState(EMPTY)
  const [query, setQuery] = useState("")
  const [accounts, setAccounts] = useState([])
  const [pods, setPods] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    setForm(EMPTY)
    setQuery("")
    setError(null)
    if (!podDialerOn) {
      setPods([])
      return
    }
    podsApi
      .list()
      .then(({ data }) => setPods((data || []).filter((pod) => pod.active)))
      .catch(() => setPods([]))
  }, [open, podDialerOn])

  useEffect(() => {
    if (!open || query.length < 2) {
      setAccounts([])
      return undefined
    }
    const timer = setTimeout(() => {
      adminApi
        .searchAccounts(query)
        .then(({ data }) => setAccounts(data || []))
        .catch(() => setAccounts([]))
    }, 300)
    return () => clearTimeout(timer)
  }, [open, query])

  function handleSubmit(event) {
    event.preventDefault()
    setError(null)
    Promise.resolve(
      onCreate({
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        owner_id: form.owner?.id,
        pod_id: form.podId || null,
      })
    ).catch((err) => setError(errorText(err)))
  }

  const canSubmit =
    form.email.trim() && form.name.trim() && form.owner && !saving

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>New texting employee</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Typography variant="body2" color="text.secondary">
              They only get the texting screen (and the dialer if they are in a
              pod). They pick their own number after logging in with the login
              link.
            </Typography>
            <TextField
              label="Email"
              type="email"
              size="small"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              required
            />
            <TextField
              label="Name"
              size="small"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />
            <Autocomplete
              options={accounts}
              value={form.owner}
              inputValue={query}
              onChange={(_, value) =>
                setForm((prev) => ({ ...prev, owner: value }))
              }
              onInputChange={(_, value) => setQuery(value)}
              getOptionLabel={(option) =>
                option?.email
                  ? `${option.name || option.email} (${option.email})`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Company account"
                  helperText="The account they text for, e.g. grace@smartsetter.io"
                />
              )}
            />
            {podDialerOn ? (
              <TextField
                select
                size="small"
                label="Pod (optional)"
                value={form.podId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, podId: e.target.value }))
                }
              >
                <MenuItem value="">No pod</MenuItem>
                {pods.map((pod) => (
                  <MenuItem key={pod.id} value={pod.id}>
                    {pod.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AdminSmsEmployeeCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  podDialerOn: PropTypes.bool,
}
